import { Check, Globe, Sparkles } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { TenantResponse } from '@/api/responses'
import { Badge, Button } from '@/components/ui'

export interface GoLiveStepProps {
  readonly tenant: TenantResponse
  readonly onContinue: () => void
}

/**
 * Uniao literal (nao `string`) para que `t(item.titleKey)` continue type-safe. O Playground deixou de
 * ser "em breve": virou um passo real do onboarding (WP-SS-01), entao saiu desta lista. Restam a
 * Descoberta Automatica de Conhecimento (SS-02) e o Agente Guiado (SS-05), ainda futuros.
 */
type ComingSoonItem =
  | {
      readonly icon: LucideIcon
      readonly titleKey: 'onboarding.goLive.comingSoon.discovery.title'
      readonly descriptionKey: 'onboarding.goLive.comingSoon.discovery.description'
    }
  | {
      readonly icon: LucideIcon
      readonly titleKey: 'onboarding.goLive.comingSoon.instantAgent.title'
      readonly descriptionKey: 'onboarding.goLive.comingSoon.instantAgent.description'
    }

const COMING_SOON_ITEMS: readonly ComingSoonItem[] = [
  {
    icon: Globe,
    titleKey: 'onboarding.goLive.comingSoon.discovery.title',
    descriptionKey: 'onboarding.goLive.comingSoon.discovery.description',
  },
  {
    icon: Sparkles,
    titleKey: 'onboarding.goLive.comingSoon.instantAgent.title',
    descriptionKey: 'onboarding.goLive.comingSoon.instantAgent.description',
  },
]

/**
 * Passo final: o tenant chegou em `Active` (saga em Ready). Explica o AgentMode inicial
 * (ObserveOnly — nunca "AI theater", docs/design/01 secao 10-11) e representa as etapas de IA
 * ainda dependentes de credencial de LLM como "proxima etapa" honesta, nunca fingida (empty
 * state = action state, docs/design/01 secao 7).
 */
export function GoLiveStep({ onContinue }: GoLiveStepProps) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col items-center gap-6 p-6 text-center sm:p-10">
      <span
        aria-hidden="true"
        className="flex h-13 w-13 items-center justify-center rounded-2xl bg-status-success-subtle text-status-success"
      >
        <Check className="h-6 w-6" />
      </span>

      <div className="flex max-w-xl flex-col gap-2">
        <h2 className="text-2xl font-semibold tracking-tight text-foreground-primary sm:text-3xl">
          {t('onboarding.goLive.title')}
        </h2>
        <p className="text-base leading-relaxed text-foreground-secondary">
          {t('onboarding.goLive.description')}
        </p>
        <p className="text-sm text-foreground-muted">{t('billing.onboarding.goLive')}</p>
      </div>

      <div className="flex w-full max-w-xl flex-col gap-1.5 rounded-xl border border-status-info/30 bg-status-info-subtle p-5 text-left">
        <span className="text-xs font-semibold uppercase tracking-wide text-status-info">
          {t('onboarding.goLive.modeLabel')}
        </span>
        <span className="text-base font-semibold text-foreground-primary">
          {t('onboarding.goLive.modeName')}
        </span>
        <p className="text-sm leading-relaxed text-foreground-secondary">
          {t('onboarding.goLive.modeDescription')}
        </p>
      </div>

      <Button onClick={onContinue}>{t('onboarding.goLive.primaryCta')}</Button>

      <div className="flex w-full max-w-xl flex-col gap-4 border-t border-border-default pt-8 text-left">
        <div className="flex flex-col gap-1">
          <h3 className="text-sm font-semibold text-foreground-primary">
            {t('onboarding.goLive.comingSoon.title')}
          </h3>
          <p className="text-sm text-foreground-muted">
            {t('onboarding.goLive.comingSoon.reason')}
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {COMING_SOON_ITEMS.map((item) => (
            <div
              key={item.titleKey}
              className="flex flex-col gap-2 rounded-lg border border-dashed border-border-default bg-background-subtle p-4"
            >
              <div className="flex items-center justify-between gap-2">
                <item.icon aria-hidden="true" className="h-5 w-5 text-foreground-muted" />
                <Badge variant="neutral">{t('onboarding.goLive.comingSoon.badge')}</Badge>
              </div>
              <p className="text-sm font-medium text-foreground-primary">{t(item.titleKey)}</p>
              <p className="text-xs text-foreground-muted">{t(item.descriptionKey)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
